import {
  addCustomLineColor,
  type LegendLabel,
  type LightStyle,
  normalizeHex,
  rememberColor,
  setSettingsLegendLabel,
} from '@better-lights/core';
import { create } from 'zustand';
import { type AppSettings, DEFAULT_APP_SETTINGS, saveSettings } from '../storage/settings.ts';

interface SettingsActions {
  patch: (p: Partial<AppSettings>) => void;
  setLegendLabel: (hex: string, label: Partial<LegendLabel>) => void;
  addCustomLineColor: (hex: string) => void;
  rememberColor: (id: string) => void;
  rememberStyle: (style: LightStyle, colorId: string) => void;
}

export type SettingsStore = AppSettings & SettingsActions & { hydrated: boolean };

export const useSettings = create<SettingsStore>((set) => ({
  ...DEFAULT_APP_SETTINGS,
  hydrated: false,
  patch: (p) => set(p),
  setLegendLabel: (hex, label) =>
    set((s) => ({ legendLabels: setSettingsLegendLabel(s.legendLabels, normalizeHex(hex), label) })),
  addCustomLineColor: (hex) =>
    set((s) => ({ customLineColors: addCustomLineColor(s.customLineColors, normalizeHex(hex)) })),
  rememberColor: (id) => set((s) => ({ recentColorIds: rememberColor(s.recentColorIds, id) })),
  rememberStyle: (style, colorId) => set({ defaultStyle: { ...style }, defaultColorId: colorId }),
}));

export function settingsData(s: SettingsStore): AppSettings {
  const {
    patch: _p,
    setLegendLabel: _l,
    addCustomLineColor: _a,
    rememberColor: _r,
    rememberStyle: _s,
    hydrated: _h,
    ...data
  } = s;
  return data;
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;

/** Loads persisted settings into the store and saves every later change (debounced). */
export function hydrateSettings(loaded: AppSettings): void {
  useSettings.setState({ ...loaded, hydrated: true });
  useSettings.subscribe((s, prev) => {
    if (s === prev) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void saveSettings(settingsData(useSettings.getState())), 200);
  });
}
